import api from './api';

/**
 * Get API tokens with pagination and filters
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @param {Object} filters - Filter criteria
 * @returns {Promise} Promise with tokens data
 */
export const getTokens = async (page = 1, limit = 10, filters = {}) => {
  const params = {
    page,
    limit,
  };

  // Add filter for active status
  if (filters.isActive !== undefined && filters.isActive !== null) {
    params.is_active = filters.isActive;
  }

  // Permission-based filters (arrays)
  if (Array.isArray(filters.services) && filters.services.length > 0) {
    params.service = filters.services;
  }
  if (Array.isArray(filters.resources) && filters.resources.length > 0) {
    params.resource = filters.resources;
  }
  if (Array.isArray(filters.actions) && filters.actions.length > 0) {
    params.action = filters.actions;
  }

  if (filters.name) params.name = filters.name;
  if (filters.ownerUserId) params.owner_user_id = filters.ownerUserId;
  if (filters.createdBy) params.created_by = filters.createdBy;
  if (filters.expiresBefore) params.expires_before = filters.expiresBefore;
  if (filters.expiredOnly) params.expired_only = 'true';
  if (filters.sortField) params.sortField = filters.sortField;
  if (filters.sortDirection) params.sortDirection = filters.sortDirection;

  const response = await api.get('/api/tokens', { params });
  return response.data;
};

/**
 * Get users who actually own or created at least one token (for Owner/Created By filters)
 * @returns {Promise} Promise with { data: [{ id, email, fullName }] }
 */
export const getTokenFilterUsers = async () => {
  const response = await api.get('/api/tokens/filter-users', { params: { isCommon: '1' } });
  return response.data;
};

/**
 * Get token by ID
 * @param {string} id - Token ID
 * @returns {Promise} Promise with token data
 */
export const getTokenById = async (id) => {
  const response = await api.get(`/api/tokens/${id}`);
  return response.data;
};

/**
 * Create new API token
 * @param {Object} tokenData - Token data
 * @param {string} tokenData.name - Token name
 * @param {string} tokenData.owner_user_id - Owner user ID
 * @param {Array<string>} tokenData.permission_ids - Permission IDs
 * @param {number} tokenData.expires_in_days - Expiration days
 * @param {string} tokenData.description - Description
 * @returns {Promise} Promise with created token (includes the actual token value)
 */
export const createToken = async (tokenData) => {
  const response = await api.post('/api/tokens', tokenData);
  return response.data;
};

/**
 * Update API token
 * @param {string} id - Token ID
 * @param {Object} updates - Updates
 * @param {string} updates.name - Token name
 * @param {string} updates.description - Description
 * @param {string} updates.expires_at - Expiration date
 * @param {Array<string>} updates.permission_ids - Permission IDs
 * @returns {Promise} Promise with updated token
 */
export const updateToken = async (id, updates) => {
  const response = await api.patch(`/api/tokens/${id}`, updates);
  return response.data;
};

/**
 * Revoke (deactivate) API token
 * @param {string} id - Token ID
 * @returns {Promise} Promise with revoked token
 */
export const revokeToken = async (id) => {
  const response = await api.delete(`/api/tokens/${id}`);
  return response.data;
};

/**
 * Toggle token status (activate/deactivate)
 * @param {string} id - Token ID
 * @returns {Promise} Promise with updated token
 */
export const toggleTokenStatus = async (id) => {
  const response = await api.patch(`/api/tokens/${id}/toggle-status`);
  return response.data;
};

/**
 * Get full token value by ID
 * @param {string} id - Token ID
 * @returns {Promise} Promise with full token data
 */
export const getTokenValue = async (id) => {
  const response = await api.get(`/api/tokens/${id}/value`);
  return response.data;
};

/**
 * Validate API token
 * @param {string} token - Token value
 * @param {string} service - Service name
 * @returns {Promise} Promise with validation result
 */
export const validateToken = async (token, service) => {
  const response = await api.post('/api/tokens/validate', {
    token,
    service,
  });
  return response.data;
};

/**
 * Self-issue a token for the current user (no permissions, selfIssued=true)
 */
export const createMyToken = async ({ tokenName, description, expiresInDays } = {}) => {
  const response = await api.post('/api/tokens/my', { tokenName, description, expiresInDays });
  return response.data;
};

/**
 * Get count of self-issued tokens with no permissions (lightweight, for badge)
 */
export const getTokenRequestsCount = async () => {
  const response = await api.get('/api/tokens/requests/count');
  return response.data;
};

/**
 * Get self-issued tokens that have no permissions yet (admin view)
 */
export const getTokenRequests = async ({ userId } = {}) => {
  const params = {};
  if (userId) params.user_id = userId;
  const response = await api.get('/api/tokens/requests', { params });
  return response.data;
};
